// Keeping a list screen current without asking anyone to press F5.
//
// Two things bring fresh data in. A timer, paused while the tab is hidden so a
// dashboard left open overnight does not hammer the API for nobody, and caught
// up the moment the tab is looked at again. And the sidebar: clicking the link
// for the screen you are already on used to do nothing at all, which reads as
// a broken button — it now asks the screen to reload.
//
// The screen supplies the refresh; it decides what "quiet" means (usually: do
// not blank the table while the request is in flight).
import { useEffect } from 'react';

/** Dispatched on `window` when the active navigation link is clicked again. */
export const NAV_REFRESH_EVENT = 'nav:refresh';

export function requestNavRefresh(): void {
  window.dispatchEvent(new Event(NAV_REFRESH_EVENT));
}

/** How often a list screen re-fetches while it is visible. */
export const REFRESH_INTERVAL_MS = 30_000;

export function useAutoRefresh(refresh: () => void, intervalMs = REFRESH_INTERVAL_MS): void {
  useEffect(() => {
    let last = Date.now();
    const run = () => {
      last = Date.now();
      refresh();
    };
    const tick = () => {
      if (document.visibilityState === 'visible') run();
    };
    // Coming back to a tab that has been hidden longer than an interval is the
    // moment the data is most likely stale; refresh at once rather than waiting
    // out the remainder of the timer.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && Date.now() - last >= intervalMs) run();
    };
    const id = setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener(NAV_REFRESH_EVENT, run);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(NAV_REFRESH_EVENT, run);
    };
  }, [refresh, intervalMs]);
}
